import { dossierMachine, type ClientRecord, type DossierStatus, type Scope } from '@clim/domain';
import type { Me } from './api-client';

export function clientName(c: Pick<ClientRecord, 'kind' | 'companyName' | 'firstName' | 'lastName' | 'civility'> | undefined): string {
  if (!c) return 'Client inconnu';
  if (c.kind === 'company') return c.companyName ?? 'Société';
  return [c.lastName?.toUpperCase(), c.firstName].filter(Boolean).join(' ') || 'Client';
}

export function statusLabel(s: DossierStatus): string {
  return dossierMachine.labels[s];
}

/** Colour family per status, for badges (UI decides the actual colours). */
export function statusTone(s: DossierStatus): 'neutral' | 'info' | 'progress' | 'success' | 'warning' | 'danger' {
  switch (s) {
    case 'draft':
      return 'neutral';
    case 'submitted':
      return 'info';
    case 'to_correct':
      return 'warning';
    case 'validated':
    case 'to_schedule':
    case 'in_progress':
      return 'progress';
    case 'completed':
      return 'success';
    case 'cancelled':
      return 'danger';
  }
}

/** Libellé d'un dossier : intitulé libre, sinon le type. */
export function dossierLabel(d: { title: string | null }, typeLabel?: string): string {
  return d.title || typeLabel || 'Dossier';
}

/**
 * UI-side permission check: only used to hide what the user cannot do. The server
 * re-checks everything; never rely on this for security.
 */
export function can(me: Pick<Me, 'permissions'> | null | undefined, perm: string, min?: Scope): boolean {
  const scope = me?.permissions[perm];
  if (!scope) return false;
  if (!min) return true;
  const rank = { own: 1, territory: 2, all: 3 } as const;
  return rank[scope] >= rank[min];
}

export function formatDate(iso: string | null | undefined, timeZone?: string): string {
  if (!iso) return '';
  return new Intl.DateTimeFormat('fr-FR', { dateStyle: 'short', timeStyle: 'short', timeZone }).format(new Date(iso));
}

/** Signature visuelle par activité (CDC §24) : couleur + icône + libellé, jamais la couleur seule. */
export const ACTIVITY_STYLE: Record<string, { color: string; icon: string; label: string }> = {
  vente: { color: '#8e24aa', icon: '€', label: 'Vente' },
  pose: { color: '#0b5cad', icon: '❄', label: 'Pose' },
  entretien: { color: '#2e7d32', icon: '🔧', label: 'Entretien' },
  sav: { color: '#e65100', icon: '⚠', label: 'SAV' },
  admin: { color: '#455a64', icon: '⚙', label: 'Administration' },
};

export function typeStyle(type: { label: string; settings?: Record<string, unknown> } | undefined) {
  const activity = (type?.settings?.activity as string | undefined) ?? 'pose';
  const base = ACTIVITY_STYLE[activity] ?? ACTIVITY_STYLE.pose!;
  return { ...base, color: (type?.settings?.color as string | undefined) ?? base.color, label: type?.label ?? base.label };
}
